/*
 * Feature Sections — a paced editorial layout in the Apple spirit.
 * Two spotlight stages (01 Assemble, 04 Ship) get full-width rows;
 * the supporting stages sit in quiet column groups between them:
 *
 *   major (text | visual) → duo → major (visual | text) → trio
 *
 * Rhythm comes from scale, not repetition. One accent whisper per
 * feature (eyebrow + a few illustration details), everything else
 * monochrome on the page's own background.
 *
 * The section is wrapped in <LazyMotion features={domAnimation}> — always
 * use `m.*` components inside this file, never `motion.*`.
 */
import {
  LazyMotion,
  Variants,
  domAnimation,
  m,
  useInView,
  useReducedMotion,
} from 'framer-motion';
import React, { useRef } from 'react';

import {
  SceneAgents,
  SceneArchitect,
  SceneEditor,
  SceneK8s,
  ScenePM,
  SceneProps,
  SceneScale,
  SceneTesting,
} from './FeatureScenes';
import './FeatureSections.css';

const EASE: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

const VIEWPORT = { once: true, amount: 0.2, margin: '0px 0px -8% 0px' } as const;

const hexRgb = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `${r}, ${g}, ${b}`;
};

const rgba = (hex: string, alpha: number): string => `rgba(${hexRgb(hex)}, ${alpha})`;

const cellVars = (accent: string) => ({
  '--cell-accent': accent,
  '--cell-rgb': hexRgb(accent),
  '--cell-soft': rgba(accent, 0.12),
  '--cell-mid': rgba(accent, 0.5),
  '--cell-line': rgba(accent, 0.35),
} as React.CSSProperties);

const rowVariants = (reduced: boolean, delay: number): Variants => {
  if (reduced) {
    return {
      hidden: { opacity: 0 },
      show: { opacity: 1, transition: { duration: 0.3 } },
    };
  }

  return {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE, delay } },
  };
};

/* ═══════════════════════════════════════════════════════════
   Feature data (titles, taglines, bullets verbatim)
   ═══════════════════════════════════════════════════════════ */

interface FeatureDef {
  id: string;
  num: string;
  act: string;
  title: string;
  tagline: string;
  bullets: string[];
  accent: string;
  Scene: React.FC<SceneProps>;
}

const FEATURES: FeatureDef[] = [
  {
    id: 'f1',
    num: '01',
    act: 'Assemble',
    title: 'Multi-Agentic Coding Agents',
    tagline: '10+ specialized AI agents collaborate like a real engineering team.',
    bullets: [
      'Tech Lead, Frontend, Backend, DevOps, and Designer agents',
      'Supervisor-based routing automatically selects the right agent',
      'Parallel streaming - multiple agents respond simultaneously',
      'Automatic retry with built-in error correction',
    ],
    accent: '#60a5fa',
    Scene: SceneAgents,
  },
  {
    id: 'f2',
    num: '02',
    act: 'Design',
    title: 'System Architect Agent',
    tagline: 'AI that designs clean, scalable architecture before writing a single line of code.',
    bullets: [
      'Auto-generates System Requirements Documents (SRD)',
      'Designs multi-tier microservice architectures from natural language',
      'Produces database schemas, API contracts, and component diagrams',
      'Continuously evolves the architecture as your project grows',
    ],
    accent: '#f0a860',
    Scene: SceneArchitect,
  },
  {
    id: 'f3',
    num: '03',
    act: 'Plan',
    title: 'Project Manager Agent',
    tagline: 'Built-in task decomposition, sprint tracking, and intelligent prioritization.',
    bullets: [
      'Automatic task breakdown from high-level descriptions',
      'Kanban board with TODO, In Progress, and Done lanes',
      'Progress tracking with sprint metrics and completion rate',
      'Parallel execution of independent sub-tasks for speed',
    ],
    accent: '#50be82',
    Scene: ScenePM,
  },
  {
    id: 'f4',
    num: '04',
    act: 'Ship',
    title: 'Managed Backend on Kubernetes',
    tagline: 'Every app gets a real backend - API, database, auth and storage - running in its own managed Kubernetes namespace.',
    bullets: [
      'Managed backend per project: API service, database, storage volumes and secrets, provisioned for you',
      'Payments built in: take payments through 8080-managed Razorpay and Stripe, or connect your own gateway',
      'Stage & Production cluster deployments out of the box - no DevOps hire, no YAML to write',
      'Microservice architecture with Docker containerization and direct Kubernetes dashboard access',
      'Stateful workloads with persistent volume claims, health checks and rolling updates',
    ],
    accent: '#60a5fa',
    Scene: SceneK8s,
  },
  {
    id: 'f5',
    num: '05',
    act: 'Verify',
    title: 'Visual Testing & Replay',
    tagline: 'AI-driven testing that sees your app like a real user does.',
    bullets: [
      'Automated browser testing with visual verification',
      'Security scan on every task: leaked secrets, vulnerable dependencies, unprotected routes',
      'Real-time session replay with full interaction recording',
      'Unit, integration, and end-to-end test generation',
      'Screenshot comparison for pixel-perfect validation',
    ],
    accent: '#f0a860',
    Scene: SceneTesting,
  },
  {
    id: 'f6',
    num: '06',
    act: 'Control',
    title: 'Integrated Code Editor',
    tagline: 'Full VSCode editor in the browser - supports every language and framework.',
    bullets: [
      'Cloud-hosted VSCode editor with syntax highlighting & IntelliSense',
      'Works with any programming language and framework',
      'Live preview with hot-reload during development',
      'Integrated terminal for full command-line access',
    ],
    accent: '#50be82',
    Scene: SceneEditor,
  },
  {
    id: 'f7',
    num: '07',
    act: 'Scale',
    title: 'Auto-Scaling Infrastructure',
    tagline: 'Your projects scale horizontally without manual intervention.',
    bullets: [
      'Horizontal pod autoscaling based on real-time metrics',
      'AI agents that run for hours handling 100M+ token projects',
      'Credit-based billing - pay only for what you use',
      'Workspace containers for isolated, secure execution',
    ],
    accent: '#60a5fa',
    Scene: SceneScale,
  },
];

/* ═══════════════════════════════════════════════════════════
   Header
   ═══════════════════════════════════════════════════════════ */

const SectionHeader: React.FC<{ reduced: boolean }> = ({ reduced }) => (
  <div className="fs-header">
    <m.p
      className="fs-header__eyebrow"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      Platform
    </m.p>
    <m.h2
      id="platform-heading"
      className="fs-header__heading"
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 20 }}
      whileInView={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease: EASE }}
    >
      Everything you need to ship
      <br />
      <span className="fs-header__sub">production-grade software.</span>
    </m.h2>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   Feature blocks
   ═══════════════════════════════════════════════════════════ */

/* Apple's feature pattern: one colored lead phrase, one sentence,
   both in the same large semibold type. Nothing else. */
const FeatText: React.FC<{ feature: FeatureDef }> = ({ feature }) => (
  <div className="fs-feat__body">
    <p className="fs-feat__eyebrow">
      <span className="fs-feat__eybnum">{feature.num} · </span>
      {feature.act}
    </p>
    <h3 className="fs-feat__copy">
      <span className="fs-feat__lead">{feature.title}.</span>{' '}
      <span className="fs-feat__rest">{feature.tagline}</span>
    </h3>
  </div>
);

/* Visuals scale and brighten in on scroll, MacBook-page style. */
const FeatVisual: React.FC<{
  feature: FeatureDef;
  active: boolean;
  reduced: boolean;
  major?: boolean;
}> = ({ feature, active, reduced, major }) => (
  <div className={`fs-feat__visual${major ? ' fs-feat__visual--major' : ''}`} aria-hidden="true">
    <m.div
      className="fs-feat__visualinner"
      initial={reduced ? false : { opacity: 0.5, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <feature.Scene active={active} reduced={reduced} />
    </m.div>
  </div>
);

const RailStop: React.FC<{ num: string; on: boolean }> = ({ num, on }) => (
  <span className="fs-railstop">
    <span className="fs-feat__railnum">{num}</span>
    <span className={`fs-node${on ? ' fs-node--on' : ''}`} />
  </span>
);

const MajorFeature: React.FC<{ feature: FeatureDef; flip?: boolean; reduced: boolean }> = ({
  feature,
  flip,
  reduced,
}) => {
  const ref = useRef<HTMLElement>(null);
  const active = useInView(ref, { amount: 0.4 });

  return (
    <m.article
      ref={ref}
      id={`feature-${feature.id}`}
      className={`fs-feat fs-feat--major${flip ? ' fs-feat--flip' : ''}`}
      style={cellVars(feature.accent)}
      variants={rowVariants(reduced, 0)}
      initial="hidden"
      whileInView="show"
      viewport={VIEWPORT}
    >
      <div className="fs-feat__rail" aria-hidden="true">
        <RailStop num={feature.num} on={active} />
      </div>
      <FeatText feature={feature} />
      <FeatVisual feature={feature} active={active && !reduced} reduced={reduced} major />
    </m.article>
  );
};

const FeatureGroup: React.FC<{ features: FeatureDef[]; kind: 'duo' | 'trio'; reduced: boolean }> = ({
  features,
  kind,
  reduced,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { amount: 0.3 });

  return (
    <div className={`fs-group fs-group--${kind}`} ref={ref}>
      <div className="fs-feat__rail fs-feat__rail--cluster" aria-hidden="true">
        {features.map((f) => (
          <span key={f.id} style={cellVars(f.accent)}>
            <RailStop num={f.num} on={inView} />
          </span>
        ))}
      </div>
      <div className="fs-group__cols">
        {features.map((f, i) => (
          <CompactFeature key={f.id} feature={f} delay={i * 0.1} reduced={reduced} />
        ))}
      </div>
    </div>
  );
};

const CompactFeature: React.FC<{ feature: FeatureDef; delay: number; reduced: boolean }> = ({
  feature,
  delay,
  reduced,
}) => {
  const ref = useRef<HTMLElement>(null);
  const active = useInView(ref, { amount: 0.3 });

  return (
    <m.article
      ref={ref}
      id={`feature-${feature.id}`}
      className="fs-feat fs-feat--compact"
      style={cellVars(feature.accent)}
      variants={rowVariants(reduced, delay)}
      initial="hidden"
      whileInView="show"
      viewport={VIEWPORT}
    >
      <FeatVisual feature={feature} active={active && !reduced} reduced={reduced} />
      <FeatText feature={feature} />
    </m.article>
  );
};

/* ═══════════════════════════════════════════════════════════
   Section root
   ═══════════════════════════════════════════════════════════ */

export const FeatureSections: React.FC = () => {
  const reduced = useReducedMotion() === true;
  const listRef = useRef<HTMLDivElement>(null);

  return (
    <LazyMotion features={domAnimation}>
      <section id="platform" className="fs-section" aria-labelledby="platform-heading">
        <div className="fs-inner">
          <SectionHeader reduced={reduced} />
          <div className="fs-list" ref={listRef}>
            <div className="fs-spine" aria-hidden="true">
              <div className="fs-spine__fill" />
            </div>
            <MajorFeature feature={FEATURES[0]} reduced={reduced} />
            <FeatureGroup features={[FEATURES[1], FEATURES[2]]} kind="duo" reduced={reduced} />
            <MajorFeature feature={FEATURES[3]} flip reduced={reduced} />
            <FeatureGroup features={[FEATURES[4], FEATURES[5], FEATURES[6]]} kind="trio" reduced={reduced} />
          </div>
        </div>
      </section>
    </LazyMotion>
  );
};
